'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWallet } from './use-wallet';
import { openDeferredEventSource } from '@/lib/open-deferred-event-source';
import { nextReconnectDelay } from '@/lib/sse-reconnect-delay';
import type { Notification } from './use-notifications';

interface NotificationsResponse {
  notifications: Notification[];
  unreadCount: number;
}

/**
 * Opens an SSE connection to /api/notifications/stream and merges
 * real-time notification events into the react-query cache used by
 * useNotifications, so the bell updates instantly.
 */
export function useNotificationStream() {
  const { address, isConnected } = useWallet();
  const queryClient = useQueryClient();
  const [isStreamConnected, setIsStreamConnected] = useState(false);
  const closeEsRef = useRef<(() => void) | null>(null);
  const reconnectRef = useRef<NodeJS.Timeout | null>(null);
  // Bounded exponential backoff. Bumped on every `onerror`, reset
  // to 0 on every successful `connected` event. Prevents the
  // notification stream from hammering a 429'd endpoint on a flat
  // 5s timer during the wallet-switch refetch storm.
  const reconnectAttemptRef = useRef(0);

  const getAccessToken = useCallback((): string | null => {
    try {
      return (
        document.cookie
          .split('; ')
          .find((row) => row.startsWith('privy-token='))
          ?.split('=')[1] ?? null
      );
    } catch {
      return null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!isConnected || !address) return;
    closeEsRef.current?.();

    const token = getAccessToken();
    if (!token) return;

    const url = `/api/notifications/stream?token=${encodeURIComponent(token)}`;
    closeEsRef.current = openDeferredEventSource(url, (es) => {
      es.addEventListener('connected', () => {
        setIsStreamConnected(true);
        reconnectAttemptRef.current = 0;
      });

      es.addEventListener('notification', (e) => {
        try {
          const notification = JSON.parse(e.data) as Notification;
          // Match the lowercased key in `use-notifications.ts` so the
          // SSE write-through hits the same observer the polling
          // query reads from. A casing mismatch here would split the
          // cache and the bell badge would lag the SSE push.
          const queryKey = ['db-notifications', address?.toLowerCase() ?? null];

          queryClient.setQueryData<NotificationsResponse>(queryKey, (old) => {
            if (!old) {
              return { notifications: [notification], unreadCount: 1 };
            }
            if (old.notifications.some((n) => n.id === notification.id)) return old;
            return {
              notifications: [notification, ...old.notifications],
              unreadCount: old.unreadCount + (notification.read ? 0 : 1),
            };
          });
        } catch { /* ignore malformed */ }
      });

      es.onerror = () => {
        setIsStreamConnected(false);
        closeEsRef.current?.();
        closeEsRef.current = null;
        const delay = nextReconnectDelay(reconnectAttemptRef.current);
        reconnectAttemptRef.current += 1;
        reconnectRef.current = setTimeout(() => connect(), delay);
      };
    });
  }, [isConnected, address, getAccessToken, queryClient]);

  useEffect(() => {
    if (isConnected && address) {
      connect();
    }
    return () => {
      closeEsRef.current?.();
      closeEsRef.current = null;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      setIsStreamConnected(false);
    };
  }, [isConnected, address, connect]);

  return { isStreamConnected };
}
